import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { Notify } from '@flexisaf/flexibull2';
import { TOP_RIGHT, ERROR, SUCCESS } from 'utils/constants';
import request from 'utils/request';
import urls from 'utils/config';
import {
  addAdminLevelFailure,
  deleteAdminLevelFailure,
  fetchLevelsFailure,
  fetchLevelsSuccess,
  fetchLevelItems,
  fetchLevelItemsSuccess,
  updateAdminLevelFailure,
  updateAdminLevelSuccess,
  updateAdminLevelItemDataFailure,
  addAdminLevelItemDataFailure,
  addAdminLevelItemDataSuccess,
  addAdminLevelSuccess,
  deleteAdminLevelSuccess,
  deleteAdminLevelItemSuccess,
  deleteAdminLevelItemFailure,
} from './actions';
import adminLevelsTypes from './types';
import { HIERARCHICAL_GROUP } from 'utils/constants';
import { networkErrorHandler } from 'utils/errrorHandlers';

const { API_BASE_URL } = urls || {};

function* fetchAdminLevels() {
  const url = `${API_BASE_URL}/administration-levels`;

  const response = yield request({
    method: 'get',
    url,
  });
  if (response.success) {
    yield put(fetchLevelsSuccess(response.raw));
  } else {
    yield put(
      fetchLevelsFailure(
        'An error occured. Please reload the page to try again.'
      )
    );

    Notify(response.message, {
      position: TOP_RIGHT,
      status: ERROR,
    });
  }
}

function* fetchAdminLevelItems() {
  const url = `${API_BASE_URL}/administration-level-items`;

  const response = yield request({
    method: 'get',
    url,
  });
  if (response.success) {
    yield put(fetchLevelItemsSuccess(response.raw));
  } else {
    yield put(
      fetchLevelsFailure(
        'An error occured. Please reload the page to try again.'
      )
    );
    networkErrorHandler(response?.statusCode, response?.message);
  }
}

function* addAdminLevel(payload) {
  const url = `${API_BASE_URL}/administration-levels`;

  const response = yield request({
    method: 'put',
    data: payload.data,
    url,
  });
  if (response.success) {
    Notify(`${HIERARCHICAL_GROUP}s saved successfully`, {
      position: TOP_RIGHT,
      status: SUCCESS,
    });
    yield put(addAdminLevelSuccess(response.raw));
  } else {
    yield put(
      addAdminLevelFailure(
        'An error occured. Please reload the page to try again.'
      )
    );
    networkErrorHandler(response?.statusCode, response?.message);
  }
}

function* addAdminLevelItemData(payload) {
  const url = `${API_BASE_URL}/administration-level-items`;

  const response = yield request({
    method: 'post',
    data: payload.data,
    url,
  });
  if (response.success) {
    Notify('Item Added Successfully!', {
      position: 'top-right',
      status: 'success',
    });
    yield put(addAdminLevelItemDataSuccess(response.raw));
  } else {

    networkErrorHandler(response?.statusCode, response?.message);
    yield put(addAdminLevelItemDataFailure(response.message));
  }
}

function* deleteAdminLevel(payload) {
  const url = `${API_BASE_URL}/administration-levels/${payload.data.id}`;

  const response = yield request({
    method: 'delete',
    url,
  });
  if (response.success) {
    Notify('Level deleted Successfully!', {
      position: 'top-right',
      status: 'success',
    });
    yield put(deleteAdminLevelSuccess(payload.data));
  } else {
    const message = response?.statusCode === 409 ? `Please delete all ${payload.data.name}s ` : response?.message
    networkErrorHandler(response?.statusCode, message);
    yield put(deleteAdminLevelFailure(payload.data));
  }
}

function* deleteAdminLevelItem({ payload }) {
  const url = `${API_BASE_URL}/administration-level-items/${payload.data.id}`;

  const response = yield request({
    method: 'delete',
    url,
  });
  if (response.success) {
    Notify('Item deleted Successfully!', {
      position: 'top-right',
      status: 'success',
    });
    payload.cb();
    yield put(deleteAdminLevelItemSuccess(payload.data));
  } else {
    networkErrorHandler(response?.statusCode, response?.message);
    yield put(deleteAdminLevelItemFailure(payload.data));
  }
}

function* updateAdminLevels(payload) {
  const url = `${API_BASE_URL}/administration-levels`;

  const response = yield request({
    method: 'put',
    data: payload.data,
    url,
  });
  if (response.success) {
    yield put(updateAdminLevelSuccess(response.raw));
    Notify(`${HIERARCHICAL_GROUP} Updated successfully`, {
      position: TOP_RIGHT,
      status: SUCCESS,
    });
  } else {
    yield put(
      updateAdminLevelFailure(
        'An error occured. Please reload the page to try again.'
      )
    );
    networkErrorHandler(response?.statusCode, response?.message);
  }
}

function* updateAdminLevelItems(payload) {
  const url = `${API_BASE_URL}/administration-level-items/${payload.data.id}`;

  const response = yield request({
    method: 'put',
    data: payload.data,
    url,
  });
  if (response.success) {
    yield put(fetchLevelItems(response.raw));
    Notify(`${HIERARCHICAL_GROUP} Item Updated successfully`, {
      position: TOP_RIGHT,
      status: SUCCESS,
    });
  } else {
    yield put(
      updateAdminLevelItemDataFailure(
        'An error occured. Please reload the page to try again.'
      )
    );
    networkErrorHandler(response?.statusCode, response?.message);
  }
}

function* onAddAdminLevels() {
  yield takeLatest(
    adminLevelsTypes.ADD_NEW_ADMIN_LEVELS_REQUEST,
    addAdminLevel
  );
}

function* onAddAdminLevelItemData() {
  yield takeLatest(
    adminLevelsTypes.ADD_NEW_ADMIN_LEVEL_ITEMS_DATA_REQUEST,
    addAdminLevelItemData
  );
}

export function* onDeleteAdminLevel() {
  yield takeEvery(
    adminLevelsTypes.DELETE_ADMIN_LEVEL_REQUEST,
    deleteAdminLevel
  );
}

export function* onDeleteAdminLevelItem() {
  yield takeEvery(
    adminLevelsTypes.DELETE_ADMIN_LEVEL_ITEMS_DATA_REQUEST,
    deleteAdminLevelItem
  );
}

function* onFetchAdminLevels() {
  yield takeLatest(
    adminLevelsTypes.FETCH_ADMIN_LEVELS_REQUEST,
    fetchAdminLevels
  );
}

function* onFetchAdminLevelItems() {
  yield takeLatest(
    adminLevelsTypes.FETCH_ADMIN_LEVEL_ITEMS_REQUEST,
    fetchAdminLevelItems
  );
}

function* onUpdateAdminLevelItems() {
  yield takeLatest(
    adminLevelsTypes.UPDATE_ADMIN_LEVEL_ITEMS_DATA_REQUEST,
    updateAdminLevelItems
  );
}

function* onUpdateAdminLevels() {
  yield takeLatest(
    adminLevelsTypes.UPDATE_ADMIN_LEVEL_REQUEST,
    updateAdminLevels
  );
}

export default function* adminLevelsSagas() {
  yield all([
    call(onAddAdminLevels),
    call(onAddAdminLevelItemData),
    call(onDeleteAdminLevel),
    call(onDeleteAdminLevelItem),
    call(onFetchAdminLevels),
    call(onFetchAdminLevelItems),
    call(onUpdateAdminLevels),
    call(onUpdateAdminLevelItems),
  ]);
}
